import React, { useEffect, useState, useMemo } from "react";
import { db, auth } from "../firebase";
import { collection, query, where, getDocs, doc, getDoc, serverTimestamp, setDoc, orderBy, limit, runTransaction } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { getOrInitStock, calculateClosing } from "../utils/stockUtils";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import { getISTDate } from "../utils/dateUtils";

const CnfStockLedger = () => {
    const navigate = useNavigate();
    const [userProfile, setUserProfile] = useState(null);
    const [skuMaster, setSkuMaster] = useState({});
    const [skuList, setSkuList] = useState([]);
    const [inventoryDate, setInventoryDate] = useState(getISTDate());
    const [inventoryRows, setInventoryRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) { navigate("/"); return; }
            const snap = await getDoc(doc(db, "users", user.uid));
            if (snap.exists()) {
                const profile = snap.data();
                if (!["superadmin", "admin", "operator"].includes(profile.role)) {
                    alert("Unauthorized access");
                    navigate("/dashboard");
                    return;
                }
                setUserProfile(profile);
            }
        });
        return () => unsub();
    }, [navigate]);

    // Load SKUs
    useEffect(() => {
        if (!userProfile) return;
        const loadSkus = async () => {
            const q = query(collection(db, "skuMaster"), where("clientId", "==", userProfile.clientId));
            let snap = await getDocs(q);
            if (snap.empty) {
                const q2 = query(collection(db, "skuMaster"));
                snap = await getDocs(q2);
            }
            const map = {};
            const list = [];
            snap.forEach((d) => {
                const data = d.data();
                const identifier = String(data.sku || data.Name || "").trim().toUpperCase();
                map[identifier] = data;
                list.push(identifier);
            });
            setSkuMaster(map);
            setSkuList(list);
        };
        loadSkus();
    }, [userProfile]);

    // Load Stock Status
    const loadLedger = async (dateStr) => {
        if (!userProfile) return;
        setLoading(true);
        try {
            // Use the centralized robust carry-forward utility
            const summaryDoc = await getOrInitStock("cnf", userProfile.clientId, dateStr, userProfile.clientId);
            const inv = summaryDoc?.inventory || {};
            
            const rows = skuList.map(sku => {
                const skuUpper = sku.trim().toUpperCase();
                const m = skuMaster[skuUpper] || {};
                const details = inv[skuUpper] || { opening: 0 };
                
                const opening = Number(details.opening || 0);
                const received = Number(details.received || 0) + Number(details.purchase || 0) + Number(details.billed || 0);
                const sold = Number(details.sold || 0) + Number(details.sale || 0) + Number(details.despatched || 0);
                const closing = opening + received - sold;
                
                return {
                    sku: skuUpper,
                    name: m.Name || skuUpper,
                    opening,
                    purchase: received,
                    sale: sold,
                    closing
                };
            });

            setInventoryRows(rows);
        } catch (err) {
            console.error("Error loading C&F ledger:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userProfile && skuList.length > 0) {
            loadLedger(inventoryDate);
        }
    }, [inventoryDate, userProfile, skuList]);

    return (
        <Layout>
            <div className="layout-content">
                <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                        <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>C&F STOCK LEDGER</h1>
                        <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Real-time inventory reconciliation for primary distribution.</p>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                         <input 
                            type="date" 
                            className="form-input" 
                            value={inventoryDate} 
                            onChange={e => setInventoryDate(e.target.value)} 
                            style={{ padding: "10px 16px" }}
                        />
                        <button onClick={() => loadLedger(inventoryDate)} className="btn-secondary">REFRESH</button>
                    </div>
                </div>

                <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
                    <table className="enterprise-table">
                        <thead>
                            <tr>
                                <th>SKU Description</th>
                                <th style={{ textAlign: "right" }}>Opening</th>
                                <th style={{ textAlign: "right", color: "var(--success)" }}>Receipt (+)</th>
                                <th style={{ textAlign: "right", color: "var(--error)" }}>Sales (-)</th>
                                <th style={{ textAlign: "right", fontWeight: 900 }}>Closing Stock</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="5" style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>Calculating balances...</td></tr>
                            ) : inventoryRows.length === 0 ? (
                                <tr><td colSpan="5" style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>No inventory data found.</td></tr>
                            ) : (
                                inventoryRows.map((r, idx) => (
                                    <tr key={idx} style={{ background: r.closing < 0 ? "rgba(239, 68, 68, 0.05)" : "transparent" }}>
                                        <td>
                                            <div style={{ fontWeight: 800, fontSize: 13, color: "var(--primary)" }}>{r.sku}</div>
                                            <div style={{ fontSize: 11, fontWeight: 600, marginTop: 2 }}>{r.name}</div>
                                        </td>
                                        <td style={{ textAlign: "right", fontWeight: 700 }}>{r.opening.toLocaleString()}</td>
                                        <td style={{ textAlign: "right", fontWeight: 700, color: "var(--success)" }}>{r.purchase.toLocaleString()}</td>
                                        <td style={{ textAlign: "right", fontWeight: 700, color: "var(--error)" }}>{r.sale.toLocaleString()}</td>
                                        <td style={{ textAlign: "right", fontWeight: 900, fontSize: 15, background: "var(--surface-hover)" }}>{r.closing.toLocaleString()}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        <tfoot>
                            <tr style={{ background: "var(--text-main)", color: "white" }}>
                                <td style={{ fontWeight: 800 }}>TOTAL AGGREGATE</td>
                                <td style={{ textAlign: "right", fontWeight: 800 }}>{inventoryRows.reduce((s, r) => s + r.opening, 0).toLocaleString()}</td>
                                <td style={{ textAlign: "right", fontWeight: 800 }}>{inventoryRows.reduce((s, r) => s + r.purchase, 0).toLocaleString()}</td>
                                <td style={{ textAlign: "right", fontWeight: 800 }}>{inventoryRows.reduce((s, r) => s + r.sale, 0).toLocaleString()}</td>
                                <td style={{ textAlign: "right", fontWeight: 900, fontSize: 16 }}>{inventoryRows.reduce((s, r) => s + r.closing, 0).toLocaleString()}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </Layout>
    );
};

export default CnfStockLedger;
